// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import {
	getArrayDiff,
	getObjectKeys,
	NullPartial,
	omitKeys,
	pickKeys,
	subsetTypeHasKey,
	ValueOf,
} from "@hex-insights/core";
import {
	PersonCreateInput,
	PersonDetailQuery,
	PersonInEmployeeCreateInput,
	PersonInGuardianCreateInput,
	PersonInStudentCreateInput,
	PersonUpdateInput,
} from "../../../Utilities";
import { EmailAddressFormConversion } from "../EmailAddress";
import { PhoneNumberFormConversion } from "../PhoneNumber";
import { UserFormConversion } from "../User";
import * as PersonFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the Person model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<K extends keyof PersonFormValues.Create = keyof PersonFormValues.Create>(
	formValues: PersonFormValues.CreateSubset<K>,
): PersonCreateInput {
	const inputFormValues = omitKeys(formValues, ["emailAddresses", "phoneNumbers", "user"]);
	const input: NullPartial<PersonCreateInput> = {
		...inputFormValues,
		emailAddresses: subsetTypeHasKey(formValues, "emailAddresses")
			? formValues.emailAddresses.map((e) => EmailAddressFormConversion.toGQLCreateInPersonInput(e))
			: undefined,
		phoneNumbers: subsetTypeHasKey(formValues, "phoneNumbers")
			? formValues.phoneNumbers.map((e) => PhoneNumberFormConversion.toGQLCreateInPersonInput(e))
			: undefined,
		user:
			subsetTypeHasKey(formValues, "user") && formValues.user.length > 0
				? UserFormConversion.toGQLCreateInPersonInput(formValues.user[0])
				: undefined,
	};
	return input as PersonCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Employee to a GraphQL nested create input for the Person model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInEmployeeInput<
	K extends keyof PersonFormValues.CreateInEmployee = keyof PersonFormValues.CreateInEmployee,
>(formValues: PersonFormValues.CreateInEmployeeSubset<K>): PersonInEmployeeCreateInput {
	const inputFormValues = omitKeys(formValues, ["emailAddresses", "phoneNumbers", "user"]);
	const input: NullPartial<PersonInEmployeeCreateInput> = {
		...inputFormValues,
		emailAddresses: subsetTypeHasKey(formValues, "emailAddresses")
			? formValues.emailAddresses.map((e) => EmailAddressFormConversion.toGQLCreateInPersonInput(e))
			: undefined,
		phoneNumbers: subsetTypeHasKey(formValues, "phoneNumbers")
			? formValues.phoneNumbers.map((e) => PhoneNumberFormConversion.toGQLCreateInPersonInput(e))
			: undefined,
		user:
			subsetTypeHasKey(formValues, "user") && formValues.user.length > 0
				? UserFormConversion.toGQLCreateInPersonInput(formValues.user[0])
				: undefined,
	};
	return input as PersonInEmployeeCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Guardian to a GraphQL nested create input for the Person model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInGuardianInput<
	K extends keyof PersonFormValues.CreateInGuardian = keyof PersonFormValues.CreateInGuardian,
>(formValues: PersonFormValues.CreateInGuardianSubset<K>): PersonInGuardianCreateInput {
	const inputFormValues = omitKeys(formValues, ["emailAddresses", "phoneNumbers", "user"]);
	const input: NullPartial<PersonInGuardianCreateInput> = {
		...inputFormValues,
		emailAddresses: subsetTypeHasKey(formValues, "emailAddresses")
			? formValues.emailAddresses.map((e) => EmailAddressFormConversion.toGQLCreateInPersonInput(e))
			: undefined,
		phoneNumbers: subsetTypeHasKey(formValues, "phoneNumbers")
			? formValues.phoneNumbers.map((e) => PhoneNumberFormConversion.toGQLCreateInPersonInput(e))
			: undefined,
		user:
			subsetTypeHasKey(formValues, "user") && formValues.user.length > 0
				? UserFormConversion.toGQLCreateInPersonInput(formValues.user[0])
				: undefined,
	};
	return input as PersonInGuardianCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Student to a GraphQL nested create input for the Person model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInStudentInput<
	K extends keyof PersonFormValues.CreateInStudent = keyof PersonFormValues.CreateInStudent,
>(formValues: PersonFormValues.CreateInStudentSubset<K>): PersonInStudentCreateInput {
	const inputFormValues = omitKeys(formValues, ["emailAddresses", "phoneNumbers", "user"]);
	const input: NullPartial<PersonInStudentCreateInput> = {
		...inputFormValues,
		emailAddresses: subsetTypeHasKey(formValues, "emailAddresses")
			? formValues.emailAddresses.map((e) => EmailAddressFormConversion.toGQLCreateInPersonInput(e))
			: undefined,
		phoneNumbers: subsetTypeHasKey(formValues, "phoneNumbers")
			? formValues.phoneNumbers.map((e) => PhoneNumberFormConversion.toGQLCreateInPersonInput(e))
			: undefined,
		user:
			subsetTypeHasKey(formValues, "user") && formValues.user.length > 0
				? UserFormConversion.toGQLCreateInPersonInput(formValues.user[0])
				: undefined,
	};
	return input as PersonInStudentCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the Person model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<K extends keyof PersonFormValues.Detail = keyof PersonFormValues.Detail>(
	formValues: Partial<PersonFormValues.DetailSubset<K>>,
	initialFormValues: PersonFormValues.DetailSubset<K>,
): PersonUpdateInput {
	const inputFormValues = omitKeys(formValues, [
		"name",
		"publicName",
		"addressIDs",
		"emailAddressIDs",
		"languageIDs",
		"nationalityIDs",
		"phoneNumberIDs",
	]);
	const input: PersonUpdateInput = { ...inputFormValues };

	if (subsetTypeHasKey(formValues, "dateOfBirth") && formValues.dateOfBirth === null) {
		input.clearDateOfBirth = true;
	}

	if (subsetTypeHasKey(formValues, "englishLanguageFluency") && formValues.englishLanguageFluency === null) {
		input.clearEnglishLanguageFluency = true;
	}

	if (subsetTypeHasKey(formValues, "employeeID") && formValues.employeeID === null) {
		input.employeeID = null;
		input.clearEmployee = true;
	}

	if (subsetTypeHasKey(formValues, "guardianID") && formValues.guardianID === null) {
		input.guardianID = null;
		input.clearGuardian = true;
	}

	if (subsetTypeHasKey(formValues, "studentID") && formValues.studentID === null) {
		input.studentID = null;
		input.clearStudent = true;
	}

	if (subsetTypeHasKey(formValues, "userID") && formValues.userID === null) {
		input.userID = null;
		input.clearUser = true;
	}

	if (
		subsetTypeHasKey(formValues, "addressIDs") &&
		subsetTypeHasKey(initialFormValues, "addressIDs") &&
		formValues.addressIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.addressIDs, formValues.addressIDs);
		input.addAddressIDs = added;
		input.removeAddressIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "emailAddressIDs") &&
		subsetTypeHasKey(initialFormValues, "emailAddressIDs") &&
		formValues.emailAddressIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.emailAddressIDs, formValues.emailAddressIDs);
		input.addEmailAddressIDs = added;
		input.removeEmailAddressIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "languageIDs") &&
		subsetTypeHasKey(initialFormValues, "languageIDs") &&
		formValues.languageIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.languageIDs, formValues.languageIDs);
		input.addLanguageIDs = added;
		input.removeLanguageIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "nationalityIDs") &&
		subsetTypeHasKey(initialFormValues, "nationalityIDs") &&
		formValues.nationalityIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.nationalityIDs, formValues.nationalityIDs);
		input.addNationalityIDs = added;
		input.removeNationalityIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "phoneNumberIDs") &&
		subsetTypeHasKey(initialFormValues, "phoneNumberIDs") &&
		formValues.phoneNumberIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.phoneNumberIDs, formValues.phoneNumberIDs);
		input.addPhoneNumberIDs = added;
		input.removePhoneNumberIDs = removed;
	}

	return input;
}

export type PersonFormKeyMap = {
	name: "name";
	publicName: "publicName";
	givenName: "givenName";
	middleName: "middleName";
	familyName: "familyName";
	nameOrder: "nameOrder";
	nickname: "nickname";
	alternateNames: "alternateNames";
	publicNameFormat: "publicNameFormat";
	imageKey: "imageKey";
	imageContentType: "imageContentType";
	gender: "gender";
	dateOfBirth: "dateOfBirth";
	englishLanguageFluency: "englishLanguageFluency";
	occupation: "occupation";
	nationalIdentificationNumber: "nationalIdentificationNumber";
	governmentDocumentSeries: "governmentDocumentSeries";
	governmentDocumentNumber: "governmentDocumentNumber";
	governmentDocumentIssuedBy: "governmentDocumentIssuedBy";
	addressIDs: "addresses";
	emailAddressIDs: "emailAddresses";
	employeeID: "employee";
	guardianID: "guardian";
	languageIDs: "languages";
	nationalityIDs: "nationalities";
	phoneNumberIDs: "phoneNumbers";
	studentID: "student";
	userID: "user";
};

export type ToFormValuesArgs<K extends keyof PersonFormValues.Detail = keyof PersonFormValues.Detail> = {
	person: Pick<PersonDetailQuery["person"], ValueOf<Pick<PersonFormKeyMap, K>>>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a Person instance to its corresponding detail form values.
 *
 * @param person The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<K extends keyof PersonFormValues.Detail = keyof PersonFormValues.Detail>({
	person,
	fields,
}: ToFormValuesArgs<K>): PersonFormValues.DetailSubset<K> {
	const formValues = {
		name: subsetTypeHasKey(person, "name") ? person.name : "",
		publicName: subsetTypeHasKey(person, "publicName") ? person.publicName : "",
		givenName: subsetTypeHasKey(person, "givenName") ? person.givenName : "",
		middleName: subsetTypeHasKey(person, "middleName") ? person.middleName : "",
		familyName: subsetTypeHasKey(person, "familyName") ? person.familyName : "",
		nameOrder: subsetTypeHasKey(person, "nameOrder") ? person.nameOrder : null,
		nickname: subsetTypeHasKey(person, "nickname") ? person.nickname : "",
		alternateNames: subsetTypeHasKey(person, "alternateNames") ? person.alternateNames : "",
		publicNameFormat: subsetTypeHasKey(person, "publicNameFormat") ? person.publicNameFormat : null,
		imageKey: subsetTypeHasKey(person, "imageKey") ? person.imageKey : "",
		imageContentType: subsetTypeHasKey(person, "imageContentType") ? person.imageContentType : "",
		gender: subsetTypeHasKey(person, "gender") ? person.gender : "",
		dateOfBirth: subsetTypeHasKey(person, "dateOfBirth") ? person.dateOfBirth : null,
		englishLanguageFluency: subsetTypeHasKey(person, "englishLanguageFluency") ? person.englishLanguageFluency : null,
		occupation: subsetTypeHasKey(person, "occupation") ? person.occupation : "",
		nationalIdentificationNumber: subsetTypeHasKey(person, "nationalIdentificationNumber")
			? person.nationalIdentificationNumber
			: "",
		governmentDocumentSeries: subsetTypeHasKey(person, "governmentDocumentSeries")
			? person.governmentDocumentSeries
			: "",
		governmentDocumentNumber: subsetTypeHasKey(person, "governmentDocumentNumber")
			? person.governmentDocumentNumber
			: "",
		governmentDocumentIssuedBy: subsetTypeHasKey(person, "governmentDocumentIssuedBy")
			? person.governmentDocumentIssuedBy
			: "",
		addressIDs: subsetTypeHasKey(person, "addresses") ? person.addresses.map((e) => e.id) : [],
		emailAddressIDs: subsetTypeHasKey(person, "emailAddresses") ? person.emailAddresses.map((e) => e.id) : [],
		employeeID: subsetTypeHasKey(person, "employee") ? person.employee?.id ?? null : null,
		guardianID: subsetTypeHasKey(person, "guardian") ? person.guardian?.id ?? null : null,
		languageIDs: subsetTypeHasKey(person, "languages") ? person.languages.map((e) => e.id) : [],
		nationalityIDs: subsetTypeHasKey(person, "nationalities") ? person.nationalities.map((e) => e.id) : [],
		phoneNumberIDs: subsetTypeHasKey(person, "phoneNumbers") ? person.phoneNumbers.map((e) => e.id) : [],
		studentID: subsetTypeHasKey(person, "student") ? person.student?.id ?? null : null,
		userID: subsetTypeHasKey(person, "user") ? person.user?.id ?? null : null,
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
